import React, { useState } from "react";
import Modal from "components/ui/modal";
import { Button, Divider } from "@mui/material";
import { Close } from "@mui/icons-material";
import useTranslate from "hooks/use-translate-language";
import propTypes from "prop-types";
import { useDispatch } from "react-redux";
import { API } from "actions/api";
import { formatAddress, formatDate } from "utils/misc";
import { toLower } from "ramda";
import { Spinner } from "components/ui";

const OrderDetailsModal = ({ orderId, mobileCard = false, customTrigger }) => {
  const translate = useTranslate();
  const dispatch = useDispatch();
  const [order, setOrder] = useState(null);
  const [isFetching, setIsFetching] = useState(false);

  const GetSupplyPeriodRequested = supplyPeriod => {
    if (supplyPeriod === 30) {
      return translate("thirtyDaysSupply");
    }
    if (supplyPeriod === 60) {
      return translate("sixtyDaysSupply");
    }
    if (supplyPeriod === 90) {
      return translate("ninetyDaysSupply");
    }
  };

  const fetchOrderDetails = async () => {
    setIsFetching(true);
    try {
      const { orderProfile } = await dispatch({
        type: API,
        route: `patient_portal/mobile/orders/${orderId}`,
        method: "GET"
      });
      setOrder(orderProfile);
    } catch (error) {
      console.error(error.message);
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <Modal
      maxWidth="sm"
      fullWidth
      aria-labelledby="order-details-modal-title"
      renderTrigger={({ openModal }) => {
        if (customTrigger) {
          return customTrigger({ openModal, fetchOrderDetails });
        }
        return (
          <a
            onClick={e => {
              e.stopPropagation();
              openModal();
              fetchOrderDetails();
            }}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                openModal();
                fetchOrderDetails();
              }
            }}
            tabIndex={0}
            role="button"
            aria-label={translate("viewDetails")}
          >
            {translate("viewDetails")}
          </a>
        );
      }}
      renderContent={({ closeModal }) => (
        <div className="order-details-modal-container">
          <Button
            onClick={closeModal}
            className="modal-close-button"
            variant="text"
            aria-label={translate("close")}
          >
            <Close aria-hidden="true" />
          </Button>

          {isFetching ? (
            <div className="modal-loading-container">
              <Spinner />
            </div>
          ) : order && (
            <div className="order-details-content">

              <div className="modal-header">
                <h2 id="order-details-modal-title"> {translate("invoiceNumber")}</h2>
                <span className="status-text">
                {order.invoice_number} | {translate(toLower(order.status))}
                </span>
              </div>

              <Divider className="modal-divider" />

              <div className="order-detail-line">
                <h3>
                  {translate("insurance")}
                </h3>
                <p>{order.compliance.current_insurance.variable}</p>
              </div>

              <Divider className="modal-divider" />

              <div className="order-detail-line">
                <h3>
                  {translate("Physician Name")}
                </h3>
                <p>{order.physician_name}</p>
              </div>           

              <Divider className="modal-divider" />

              <div className="order-detail-line">
                <h3>
                  {translate("equipmentOrdered")}:
                </h3>
                <p>
                  {order.requested_equipment
                    .map(equipment => translate(equipment))
                    .join(", ")}
                </p>
              </div>

              <Divider className="modal-divider" />

              <div className="order-detail-line">
                <h3>
                  {translate("orderDate")}
                </h3>
                <p>{formatDate(order.order_date)}</p>
              </div>

              <Divider className="modal-divider" />

              <div className="order-detail-line">
                <h3>
                  {translate("shippingAddress")}
                </h3>
                <p>
                  {formatAddress(order.address)}
                </p>
              </div>

              <Divider className="modal-divider" />

              {order.tracking_number && (
                <>
                  <div className="order-detail-line">
                    <h3>
                      {translate("trackingNumber")}
                    </h3>
                    <p>{order.tracking_number}</p>
                  </div>
                  <Divider className="modal-divider" />
                </>
              )}

              <div className="order-detail-line">
                <h3>
                  {translate("quantityRequested")}
                </h3>
                <p>
                  {GetSupplyPeriodRequested(order.supply_period)}
                </p>
              </div>

            </div>
          )}
        </div>
      )}
    />
  );
};

OrderDetailsModal.propTypes = {
  orderId: propTypes.string.isRequired,
  mobileCard: propTypes.bool,
  customTrigger: propTypes.func
};

export default OrderDetailsModal;

